import React from "react";
import { Check, MoreHorizontal } from "lucide-react";
import {
  PIPELINE_STAGE_IDS,
} from "../constants/pipelineStages";
import { STAGE_PRIMARY_ACTION } from "../constants/pipelineCardActions";
import {
  formatEngagementPercent,
  formatFollowers,
  formatRateInr,
  getInitials,
} from "../utils/pipelineFormatters";

/**
 * @param {Object} props
 * @param {import('../hooks/usePipelineBoard').PipelineCardModel} props.card
 * @param {string} props.dragType
 * @param {(cardId: string) => void} [props.onPrimaryAction]
 * @param {(cardId: string) => void} [props.onMore]
 * @param {(cardId: string) => void} [props.onOpen]
 */
function PipelineCard({
  card,
  onPrimaryAction,
  onMore,
  onOpen,
}) {
  const negotiationAgreed =
    card.stageId === PIPELINE_STAGE_IDS.NEGOTIATING &&
    card.negotiation?.status === "agreed";
  const brandSigned = Boolean(card?.contract?.brandSignedAt);
  const influencerSigned = Boolean(card?.contract?.influencerSignedAt);
  const fullySigned = brandSigned && influencerSigned;
  const agreedRateInr =
    card.contract?.agreedRateInr != null && Number.isFinite(Number(card.contract.agreedRateInr))
      ? Number(card.contract.agreedRateInr)
      : null;
  const agreedRateDisplay =
    agreedRateInr != null
      ? agreedRateInr
      : negotiationAgreed && card.negotiation?.proposedRateInr != null
        ? Number(card.negotiation.proposedRateInr)
        : null;

  const primaryLabel =
    card.stageId === PIPELINE_STAGE_IDS.NEGOTIATING && negotiationAgreed
      ? "View agreed rate"
      : card.stageId === PIPELINE_STAGE_IDS.CONTRACTED && fullySigned
        ? "Mark confirmed"
      : STAGE_PRIMARY_ACTION[card.stageId] ?? "Action";
  const isRejected = card.stageId === PIPELINE_STAGE_IDS.REJECTED;

  const tags = [...card.niches, ...card.platforms];

  /** @type {Record<string, string>} */
  const stageAccentClasses = {
    [PIPELINE_STAGE_IDS.SHORTLISTED]: "bg-[#9C9790]",
    [PIPELINE_STAGE_IDS.CONTACTED]: "bg-[#F08A24]",
    [PIPELINE_STAGE_IDS.NEGOTIATING]: "bg-[#4C43B0]",
    [PIPELINE_STAGE_IDS.CONTRACTED]: "bg-[#1557A0]",
    [PIPELINE_STAGE_IDS.CONFIRMED]: "bg-[#1D9E75]",
    [PIPELINE_STAGE_IDS.REJECTED]: "bg-[#D14343]",
  };

  const accentClass =
    stageAccentClasses[card.stageId] ?? "bg-[#9C9790]";

  const statusLabel =
    card.stageId === PIPELINE_STAGE_IDS.CONTACTED
      ? "Awaiting influencer acceptance"
      : card.stageId === PIPELINE_STAGE_IDS.NEGOTIATING
        ? negotiationAgreed && agreedRateDisplay != null
          ? `Rate agreed: ${formatRateInr(agreedRateDisplay)}`
          : negotiationAgreed
            ? "Rate agreed"
            : "Pricing under discussion"
        : card.stageId === PIPELINE_STAGE_IDS.CONTRACTED
          ? fullySigned
            ? "Both signed ✓"
            : brandSigned
              ? "Brand signed · Awaiting influencer"
              : "Brand signature needed"
          : card.stageId === PIPELINE_STAGE_IDS.CONFIRMED
            ? "Now it's live"
            : card.stageId === PIPELINE_STAGE_IDS.REJECTED
              ? "Removed or declined for this campaign"
            : "Invite link not yet sent";

  return (
    <article
      onClick={() => onOpen?.(card.id)}
      className="relative flex bg-white rounded-lg border border-[#E3DFD9] shadow-sm cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
    >
      <div
        className={`w-1 sm:w-1 shrink-0 ${accentClass}`}
        aria-hidden
      />
      <div className="flex-1 p-3.5 sm:p-4">
        <div className="flex gap-3 mb-3">
          <div className="w-11 h-11 rounded-full bg-[#FFF0E8] flex items-center justify-center shrink-0">
            <span className="text-xs sm:text-sm font-bold text-[#E65C1A]">
              {getInitials(card.name)}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-[#1A1714] truncate text-[13px] sm:text-sm">
              {card.name}
            </h3>
            <p className="text-[11px] text-[#A09890] truncate">
              {card.handle}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {tags.map((t) => (
            <span
              key={t}
              className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[#F0EDE9] text-[#6B6560]"
            >
              {t}
            </span>
          ))}
        </div>

        <div
          className={
            negotiationAgreed
              ? "mb-3 rounded-lg border border-[#B7DFC3] bg-[#E6F4EA] px-3 py-2 flex items-center gap-2"
              : "mb-3 rounded-lg border border-[#E3DFD9] bg-[#FFFBF8] px-3 py-2 flex items-center gap-2"
          }
        >
          <span
            className={`h-2 w-2 rounded-full shrink-0 ${negotiationAgreed ? "bg-[#1E8E3E]" : accentClass}`}
            aria-hidden
          />
          <span
            className={`text-[11px] font-semibold truncate ${
              negotiationAgreed ? "text-[#1E8E3E]" : "text-[#6B6560]"
            }`}
          >
            {statusLabel}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4 text-center border-y border-[#E3DFD9] py-2">
          <div>
            <p className="text-[9px] font-semibold text-[#A09890] uppercase tracking-[0.14em]">
              Followers
            </p>
            <p className="text-sm font-bold text-[#1A1714] tabular-nums">
              {formatFollowers(card.followers)}
            </p>
          </div>
          <div>
            <p className="text-[9px] font-semibold text-[#A09890] uppercase tracking-[0.14em]">
              Engmt
            </p>
            <p className="text-sm font-bold text-[#1A1714] tabular-nums">
              {formatEngagementPercent(card.engagementRate)}
            </p>
          </div>
          <div>
            <p className="text-[9px] font-semibold text-[#A09890] uppercase tracking-[0.14em]">
              {negotiationAgreed ? "Agreed" : "Rate"}
            </p>
            <p className="text-sm font-bold text-[#1A1714] tabular-nums truncate">
              {negotiationAgreed
                ? agreedRateDisplay != null
                  ? formatRateInr(agreedRateDisplay)
                  : "—"
                : formatRateInr(card.rate)}
            </p>
          </div>
        </div>

        <div className="flex items-stretch gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPrimaryAction?.(card.id);
            }}
            disabled={isRejected}
            className={
              isRejected
                ? "flex-1 min-w-0 px-2 py-2 rounded-lg text-[11px] font-semibold text-[#6B6560] bg-[#F5F5F5] border border-[#E3DFD9] inline-flex items-center justify-center gap-1.5 cursor-not-allowed"
              : card.stageId === PIPELINE_STAGE_IDS.NEGOTIATING && negotiationAgreed
                ? "flex-1 min-w-0 px-2 py-2 rounded-lg text-[11px] font-semibold text-white bg-[#D1612E] hover:bg-[#B85528] transition-colors inline-flex items-center justify-center gap-1.5"
                : "flex-1 min-w-0 px-2 py-2 rounded-lg text-[11px] font-semibold text-white bg-orange-600 hover:bg-orange-800 transition-colors inline-flex items-center justify-center gap-1.5"
            }
          >
            {card.stageId === PIPELINE_STAGE_IDS.NEGOTIATING && negotiationAgreed ? (
              <>
                <Check className="w-3 h-3 text-white shrink-0" strokeWidth={2.5} aria-hidden />
                <span className="truncate">{primaryLabel}</span>
              </>
            ) : (
              <span className="truncate">{primaryLabel}</span>
            )}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMore?.(card.id);
            }}
            className="w-9 sm:w-10 shrink-0 flex items-center justify-center rounded-lg border border-[#E3DFD9] text-[#A09890] bg-white hover:bg-[#FFF0E8] transition-colors"
            aria-label="More options"
          >
            <MoreHorizontal className="w-3 h-3" />
          </button>
        </div>
      </div>
    </article>
  );
}

export default PipelineCard;
